// Shared parsing + safety filter for AI chat actions.
// Extracted so it can be unit-tested independently of the chat UI.
//
// IMPORTANT: This is the LAST line of defense against a jailbroken model
// emitting actions that link to /admin, mailto:, javascript:, or arbitrary
// external domains. Keep the allowlist in sync with PUBLIC_ROUTES in
// supabase/functions/ai-chat/index.ts.

export type ActionKind = "link" | "external" | "whatsapp" | "messenger" | "email";
export type Action = { label: string; kind: ActionKind; href?: string };

export const ALLOWED_ROUTES = new Set<string>([
  "/", "/about", "/projects", "/fundraisers", "/donate", "/sponsor",
  "/volunteer", "/transparency", "/news", "/contact", "/help", "/faq",
  "/the-circle", "/partners", "/start-fundraiser", "/events",
  "/little-helpers", "/impact-squad", "/newsletters",
]);

// Hosts the assistant is allowed to deep-link to for `external` actions.
// Anything else (mailto, javascript, data, ftp, arbitrary https) is rejected.
export const ALLOWED_EXTERNAL_HOSTS = new Set<string>([
  "raisesrilanka.org",
  "www.raisesrilanka.org",
  "wa.me",
  "m.me",
  "facebook.com",
  "www.facebook.com",
  "venmo.com",
  "www.venmo.com",
  "instagram.com",
  "www.instagram.com",
]);

export function isSafeAction(a: unknown): a is Action {
  if (!a || typeof a !== "object") return false;
  const action = a as Partial<Action>;
  if (typeof action.label !== "string" || !action.label.trim()) return false;
  if (typeof action.kind !== "string") return false;

  if (action.kind === "whatsapp" || action.kind === "messenger" || action.kind === "email") {
    // These resolve from site_settings client-side; the model never supplies the target.
    return true;
  }

  if (action.kind === "link") {
    const href = action.href ?? "";
    if (typeof href !== "string" || !href.startsWith("/")) return false;
    // Reject anything that could trick the router into admin/API surface or
    // protocol smuggling like "/\evil.com" or "/javascript:alert(1)".
    if (/[\\\s]/.test(href)) return false;
    if (href.startsWith("//")) return false;
    if (href.startsWith("/admin") || href.startsWith("/api")) return false;
    if (/[?#]/.test(href)) {
      // Disallow query/hash to keep the allowlist trivially exact.
      return false;
    }
    if (ALLOWED_ROUTES.has(href)) return true;
    // Allow detail slugs only for known public collections.
    return /^\/(projects|news|fundraisers|events)\/[a-z0-9-]+$/i.test(href);
  }

  if (action.kind === "external") {
    const href = action.href ?? "";
    if (typeof href !== "string") return false;
    let url: URL;
    try {
      url = new URL(href);
    } catch {
      return false;
    }
    if (url.protocol !== "https:") return false;
    return ALLOWED_EXTERNAL_HOSTS.has(url.hostname.toLowerCase());
  }

  return false;
}

const ACTION_FENCE = /```actions\s*([\s\S]*?)```\s*$/i;

export function parseActions(content: string): { text: string; actions: Action[] } {
  if (typeof content !== "string") return { text: "", actions: [] };
  const m = content.match(ACTION_FENCE);
  if (!m) return { text: content, actions: [] };
  try {
    const parsed = JSON.parse(m[1].trim());
    if (!Array.isArray(parsed)) return { text: content, actions: [] };
    const actions = parsed.filter(isSafeAction).slice(0, 4);
    return { text: content.slice(0, m.index).trimEnd(), actions };
  } catch {
    return { text: content, actions: [] };
  }
}

// Heuristic: detect assistant responses that admit they can't answer from
// public content, so the UI can surface the "Talk to a human" CTA inline.
const DEFER_PATTERNS = [
  /i (?:don't|do not|cannot|can't) (?:have|find|access|share|provide)/i,
  /not (?:in|available in|covered in|part of) (?:our )?public/i,
  /i'?m not (?:sure|able)/i,
  /reach out to (?:our )?(?:team|volunteers?|contact)/i,
  /contact (?:us|our team|a volunteer)/i,
  /talk to a human/i,
  /unable to (?:help|answer|assist)/i,
];

export function shouldOfferHuman(text: string): boolean {
  if (!text || text.length < 8) return false;
  return DEFER_PATTERNS.some((re) => re.test(text));
}
